import posthog from 'posthog-js';
import { analytics as proxyAnalytics } from './analytics-proxy';
import { performantAnalytics } from './analytics-non-blocking';

// Analytics utility functions with automatic fallback to proxy

export const AnalyticsEvents = {
    // Authentication Events
    AUTH_STARTED: 'auth_started',
    AUTH_METHOD_SELECTED: 'auth_method_selected',
    AUTH_SUCCEEDED: 'auth_succeeded',
    AUTH_FAILED: 'auth_failed',
    USER_CREATED: 'user_created',
    USER_SIGNED_OUT: 'user_signed_out',

    // Pairing Flow Events
    PAIRING_STARTED: 'pairing_started',
    PAIRING_STEP_COMPLETED: 'pairing_step_completed',
    QR_SCANNER_OPENED: 'qr_scanner_opened',
    QR_CODE_SCANNED: 'qr_code_scanned',
    MANUAL_ENTRY_OPENED: 'manual_entry_opened',
    DEVICE_CODE_ENTERED: 'device_code_entered',
    DEVICE_PAIRED: 'device_paired',
    PAIRING_FAILED: 'pairing_failed',
    PAIRING_SKIPPED: 'pairing_skipped',
    PAIRING_COMPLETED: 'pairing_completed',
    SAVE_FOR_LATER_CLICKED: 'save_for_later_clicked',

    // AI Curation Events
    AI_CURATION_STARTED: 'ai_curation_started',
    AI_CURATION_SELECTED: 'ai_curation_selected',
    AI_DESCRIPTION_SUBMITTED: 'ai_description_submitted',
    AI_GENRES_SELECTED: 'ai_genres_selected',
    AI_CHANNELS_GENERATED: 'ai_channels_generated',
    AI_CHANNEL_SELECTED: 'ai_channel_selected',
    AI_CURATION_SKIPPED: 'ai_curation_skipped',

    // Artcast/Channel Events
    ARTCAST_BROWSING_STARTED: 'artcast_browsing_started',
    ARTCAST_FILTERED: 'artcast_filtered',
    ARTCAST_PREVIEWED: 'artcast_previewed',
    ARTCAST_SELECTED: 'artcast_selected',
    ARTCAST_SAVED_FOR_LATER: 'artcast_saved_for_later',
    ARTCAST_SELECTION_SKIPPED: 'artcast_selection_skipped',
    SAVED_QUEUE_SELECTED: 'saved_queue_selected',

    // Preview Pass Events
    PREVIEW_PASS_OFFERED: 'preview_pass_offered',
    PREVIEW_PASS_ACCEPTED: 'preview_pass_accepted',
    PREVIEW_PASS_DECLINED: 'preview_pass_declined',
    PREVIEW_PASS_ACTIVATED: 'preview_pass_activated',

    // Success Events
    PAIRING_SUCCESS_SHOWN: 'pairing_success_shown',
    APP_DOWNLOAD_CLICKED: 'app_download_clicked',
    ENJOY_STEP_REACHED: 'enjoy_step_reached',

    // Error Events
    ERROR_OCCURRED: 'error_occurred',
    API_ERROR: 'api_error',

    // Performance Events
    PERFORMANCE_METRIC: 'performance_metric',
    PAGE_LOAD_TIME: 'page_load_time',
    API_RESPONSE_TIME: 'api_response_time',

    // Max AI Events
    MAX_AI_CONTEXT_LOADED: 'max_ai_context_loaded',
    MAX_AI_CONTEXT_UPDATED: 'max_ai_context_updated',
    MAX_AI_CONTEXT_CLEARED: 'max_ai_context_cleared',
    MAX_AI_PREFERENCE_ADDED: 'max_ai_preference_added',
    MAX_AI_INTERACTION_TRACKED: 'max_ai_interaction_tracked',
    MAX_AI_HELP_REQUESTED: 'max_ai_help_requested',
    MAX_AI_SUGGESTION_ACCEPTED: 'max_ai_suggestion_accepted',
    MAX_AI_SUGGESTION_DISMISSED: 'max_ai_suggestion_dismissed',
} as const;

// Helper to track page performance (NON-BLOCKING)
export function trackPagePerformance() {
    if (typeof window !== 'undefined' && window.performance) {
        const perfData = window.performance.getEntriesByType(
            'navigation'
        )[0] as PerformanceNavigationTiming;
        if (perfData) {
            const pageLoadTime = perfData.loadEventEnd - perfData.fetchStart;
            const domContentLoadedTime =
                perfData.domContentLoadedEventEnd - perfData.fetchStart;
            const firstContentfulPaint =
                performance.getEntriesByName('first-contentful-paint')[0]
                    ?.startTime || 0;

            // Fire and forget - use performant analytics for zero blocking
            performantAnalytics.capture(AnalyticsEvents.PAGE_LOAD_TIME, {
                page_load_time: Math.round(pageLoadTime),
                dom_content_loaded_time: Math.round(domContentLoadedTime),
                first_contentful_paint: Math.round(firstContentfulPaint),
                page: window.location.pathname,
            });
        }
    }
}

// Helper to track API response times (NON-BLOCKING)
export function trackAPIResponseTime(endpoint: string, startTime: number) {
    const responseTime = Date.now() - startTime;
    // Fire and forget - use performant analytics for zero blocking
    performantAnalytics.capture(AnalyticsEvents.API_RESPONSE_TIME, {
        endpoint,
        response_time: responseTime,
        page: window.location.pathname,
    });
}

// Helper to get session context
export function getSessionContext() {
    return {
        session_id: posthog?.sessionRecording?.sessionId,
        window_id: (posthog?.sessionRecording as any)?.windowId,
        referrer: document.referrer,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
    };
}

// Session Recording Management Functions
export function startSessionRecording() {
    if (typeof window !== 'undefined' && posthog) {
        try {
            // If we previously opted out, opt back in
            if (
                posthog.has_opted_out_capturing &&
                posthog.has_opted_out_capturing()
            ) {
                posthog.opt_in_capturing();
                console.log('[Analytics] Opted back into capturing');
            }

            // Start session recording
            if (typeof posthog.startSessionRecording === 'function') {
                posthog.startSessionRecording();
                console.log('[Analytics] Session recording started (method 1)');
            } else if (
                (posthog as any).sessionRecording &&
                typeof (posthog as any).sessionRecording.start === 'function'
            ) {
                (posthog as any).sessionRecording.start();
                console.log('[Analytics] Session recording started (method 2)');
            } else {
                console.warn(
                    '[Analytics] No session recording start method available'
                );
            }

            // Verify it's actually recording
            setTimeout(() => {
                const isRecording =
                    (posthog as any).isSessionRecordingEnabled?.() || false;
                console.log(
                    '[Analytics] Recording status after start:',
                    isRecording
                );
            }, 500);
        } catch (error) {
            console.warn(
                '[Analytics] Failed to start session recording:',
                error
            );
        }
    }
}

export function stopSessionRecording(permanent: boolean = false) {
    if (typeof window !== 'undefined' && posthog) {
        try {
            // PostHog's API for stopping recording
            if (typeof posthog.stopSessionRecording === 'function') {
                posthog.stopSessionRecording();
                console.log('[Analytics] Session recording stopped (method 1)');
            } else if (
                (posthog as any).sessionRecording &&
                typeof (posthog as any).sessionRecording.stop === 'function'
            ) {
                (posthog as any).sessionRecording.stop();
                console.log('[Analytics] Session recording stopped (method 2)');
            } else if (permanent) {
                // For logout scenarios where we need to ensure complete stop
                // This will stop all tracking, not just recording
                console.warn(
                    '[Analytics] Using opt-out to stop recording (will stop all tracking)'
                );
                posthog.opt_out_capturing();
            }

            // Force disable recording in config as well
            if (permanent && posthog.config) {
                (posthog as any).config.disable_session_recording = true;
            }
        } catch (error) {
            console.warn(
                '[Analytics] Failed to stop session recording:',
                error
            );
        }
    }
}

export function isSessionRecordingActive(): boolean {
    if (typeof window !== 'undefined' && posthog && posthog.sessionRecording) {
        try {
            return (posthog as any).isSessionRecordingEnabled?.() || false;
        } catch (error) {
            console.warn(
                '[Analytics] Failed to check session recording status:',
                error
            );
            return false;
        }
    }
    return false;
}

// Get the current session recording ID
export function getSessionRecordingId(): string | null {
    if (typeof window !== 'undefined' && posthog && posthog.sessionRecording) {
        try {
            return posthog.get_session_id() || null;
        } catch (error) {
            console.warn(
                '[Analytics] Failed to get session recording ID:',
                error
            );
            return null;
        }
    }
    return null;
}

// Helper to identify user from stored data (NON-BLOCKING)
export function identifyStoredUser() {
    if (typeof window !== 'undefined') {
        const userId = localStorage.getItem('pair_user_id');
        const userEmail = localStorage.getItem('pair_user_email');

        if (userId) {
            // Fire and forget - use performant analytics for zero blocking
            performantAnalytics.identify(userId, {
                email: userEmail,
            });
        }
    }
}

// Build user properties object for PostHog identification
function buildUserProperties(email: string, userData?: any) {
    const properties: Record<string, any> = {
        email: email,
    };

    // Add email domain for filtering
    if (email && email.includes('@')) {
        properties.email_domain = email.split('@')[1];
    }

    // Add core user properties if provided
    if (userData) {
        if (userData.name) properties.name = userData.name;
        if (userData.first_name) properties.first_name = userData.first_name;
        if (userData.last_name) properties.last_name = userData.last_name;
        if (userData.profile_path)
            properties.profile_path = userData.profile_path;
        if (userData.test_account !== undefined)
            properties.test_account = userData.test_account;
        if (userData.plan_display_name)
            properties.plan_display_name = userData.plan_display_name;
        if (userData.plan_type) properties.plan_type = userData.plan_type;
        if (userData.account_type)
            properties.account_type = userData.account_type;
        if (userData.plan_upgradable !== undefined)
            properties.plan_upgradable = userData.plan_upgradable;
    }

    // Add environment info
    properties.environment =
        process.env.NODE_ENV === 'production' ? 'production' : 'development';

    return properties;
}

// Handle user login - start fresh recording session
export function handleUserLogin(userId: string, email: string, userData?: any) {
    if (typeof window !== 'undefined' && posthog) {
        try {
            console.log('[Analytics] Starting login process...');

            // Store user data for session persistence
            localStorage.setItem('pair_user_id', userId);
            localStorage.setItem('pair_user_email', email);

            // Build comprehensive user properties
            const userProperties = buildUserProperties(email, userData);

            // Identify the user in PostHog with full properties
            posthog.identify(userId, userProperties);

            console.log('[Analytics] User identified with properties:', {
                userId,
                properties: userProperties,
            });

            // Start a new session recording for the authenticated user
            startSessionRecording();

            // Track login event
            performantAnalytics.capture(AnalyticsEvents.AUTH_SUCCEEDED, {
                timestamp: new Date().toISOString(),
                user_id: userId,
                email: email,
                session_id: getSessionRecordingId(),
            });

            console.log('[Analytics] User login complete:', {
                userId,
                sessionId: getSessionRecordingId(),
                isRecording: isSessionRecordingActive(),
            });
        } catch (error) {
            console.error(
                '[Analytics] Failed to handle login properly:',
                error
            );
        }
    }
}

// Update user properties on session restore (without starting new session)
export function updateUserProperties(
    userId: string,
    email: string,
    userData?: any
) {
    if (typeof window !== 'undefined' && posthog) {
        try {
            console.log(
                '[Analytics] Updating user properties on session restore...'
            );

            // Build comprehensive user properties
            const userProperties = buildUserProperties(email, userData);

            // Identify the user in PostHog with updated properties
            posthog.identify(userId, userProperties);

            console.log('[Analytics] User properties updated:', {
                userId,
                properties: userProperties,
            });
        } catch (error) {
            console.error(
                '[Analytics] Failed to update user properties:',
                error
            );
        }
    }
}

// Handle user logout - stop recording and reset session
export function handleUserLogout() {
    if (typeof window !== 'undefined' && posthog) {
        try {
            console.log('[Analytics] Starting logout process...');

            // Track the logout event first with current session context
            const currentSessionId = getSessionRecordingId();
            performantAnalytics.capture(AnalyticsEvents.USER_SIGNED_OUT, {
                timestamp: new Date().toISOString(),
                session_ending: currentSessionId,
                ...getSessionContext(),
            });

            // Force stop the current session recording permanently
            console.log('[Analytics] Stopping current session recording...');
            stopSessionRecording(true); // Use permanent flag for logout

            // Clear stored user data BEFORE reset
            localStorage.removeItem('pair_user_id');
            localStorage.removeItem('pair_user_email');

            // Force end the current session by resetting PostHog completely
            if (typeof posthog.reset === 'function') {
                console.log('[Analytics] Resetting PostHog session...');
                posthog.reset();

                // Small delay to ensure reset completes
                setTimeout(() => {
                    // Generate a new distinct ID for the anonymous session
                    const newAnonymousId = `anon_${Date.now()}_${Math.random()
                        .toString(36)
                        .substr(2, 9)}`;

                    // Start fresh with a new anonymous session
                    if (typeof posthog.identify === 'function') {
                        posthog.identify(newAnonymousId);
                        console.log(
                            '[Analytics] New anonymous session started:',
                            newAnonymousId
                        );
                    }

                    // Start recording for the new anonymous session
                    startSessionRecording();

                    // Verify new session is different
                    const newSessionId = getSessionRecordingId();
                    console.log('[Analytics] Session transition complete:', {
                        oldSession: currentSessionId,
                        newSession: newSessionId,
                        isRecording: isSessionRecordingActive(),
                    });
                }, 100);
            } else {
                // Fallback if reset is not available
                console.warn(
                    '[Analytics] PostHog reset not available, using fallback'
                );

                // Force a page reload to ensure clean session
                // This is a last resort but ensures a clean break
                window.location.reload();
            }
        } catch (error) {
            console.error(
                '[Analytics] Failed to handle logout properly:',
                error
            );
            // As a last resort, reload the page to ensure clean session
            window.location.reload();
        }
    }
}

// Helper for error tracking with context (NON-BLOCKING)
export function trackErrorWithContext(
    error: Error | string,
    context?: Record<string, any>
) {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stackTrace = typeof error === 'object' ? error.stack : undefined;

    // Fire and forget - use performant analytics for zero blocking
    performantAnalytics.capture(AnalyticsEvents.ERROR_OCCURRED, {
        error_message: errorMessage,
        stack_trace: stackTrace,
        page: window.location.pathname,
        ...context,
        ...getSessionContext(),
    });
}

// Handle page unload - ensure recording data is sent before leaving
export function handlePageUnload() {
    if (typeof window !== 'undefined' && posthog) {
        try {
            // Force flush any pending data to ensure it gets sent
            if (typeof (posthog as any).capture === 'function') {
                // Send a page leave event
                posthog.capture('$pageleave', {
                    timestamp: new Date().toISOString(),
                });
            }

            // Note: We don't stop recording here as PostHog handles this
            // The session will timeout after 15 minutes of inactivity (our configured value)
        } catch (error) {
            // Silently fail - we're unloading anyway
        }
    }
}

// Set up automatic page unload handler
if (typeof window !== 'undefined') {
    // Use both beforeunload and pagehide for better browser coverage
    window.addEventListener('beforeunload', handlePageUnload);
    window.addEventListener('pagehide', handlePageUnload);

    // Also handle visibility changes for mobile browsers
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            handlePageUnload();
        }
    });
}

// Export performant analytics with guaranteed non-blocking behavior
export const analytics = performantAnalytics;
